/**
 * Merko Integrations — import-profile shaping and validation.
 *
 * Two things live here because both are easy to get wrong in a way the backend
 * answers with a flat 400:
 *
 *   • **PATCH is a full replacement.** A profile returned by GET carries
 *     backend metadata — `id`, `status`, `next_run_at`, `active_job`,
 *     `error_code`, and a response-only `attested_at` inside the permission —
 *     none of which may be sent back. `toProfileInput()` is the only sanctioned
 *     way to turn a `Profile` into something submittable.
 *   • **Permission is mandatory and deliberate.** `source` means explicit
 *     provider permission evidence, not an assumption of consent; `attested`
 *     is an owner-written statement that does not override provider negatives
 *     or existing Merko suppression.
 */

import { CONTACT_FIELDS, MAX_IDENTIFIER_LENGTH } from "./types";
import type { ContactField, Frequency, Permission, Profile, ProfileInput } from "./types";
import { validateSource } from "./sources";

export const FREQUENCIES: readonly Frequency[] = ["off", "hourly", "daily"] as const;

/** Attestation length bounds, from the handoff. */
export const ATTESTATION_MIN = 10;
export const ATTESTATION_MAX = 500;

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/**
 * Strip a `Profile` down to exactly the fields PATCH accepts.
 *
 * Built by naming the six wanted fields rather than deleting the unwanted
 * ones: a new piece of response metadata then stays out by default instead of
 * leaking into the next PATCH until someone notices.
 */
export function toProfileInput(profile: Profile): ProfileInput {
    return {
        app_id: profile.app_id,
        group_id: profile.group_id,
        source: { ...profile.source },
        mapping: { ...profile.mapping },
        permission: stripPermission(profile.permission),
        frequency: profile.frequency,
    };
}

/** Drops the response-only `attested_at` the backend adds to an attestation. */
export function stripPermission(permission: Permission & { attested_at?: number }): Permission {
    switch (permission?.mode) {
        case "mapped":
            return { mode: "mapped", field: permission.field };
        case "attested":
            return { mode: "attested", attestation: permission.attestation };
        case "source":
            return { mode: "source" };
        default:
            // An unknown mode is a contract change, not something to guess at.
            throw new Error(`Unsupported permission mode: ${String((permission as any)?.mode)}`);
    }
}

export interface ProfileValidation {
    valid: boolean;
    /** Keyed by form section, so the right control can be highlighted. */
    errors: Partial<Record<"group" | "source" | "mapping" | "permission" | "frequency", string>>;
    /** Per-source-field detail, when the source itself is wrong. */
    sourceErrors: Record<string, string>;
    /** Per-contact-field detail, when a mapping value is wrong. */
    mappingErrors: Partial<Record<ContactField, string>>;
}

/**
 * Check a profile locally before submitting it.
 *
 * Deliberately does not reimplement the provider field-name syntax that Zoho,
 * HubSpot and Salesforce are additionally validated against server-side — the
 * handoff is explicit that the UI must not construct raw provider queries, so
 * that check stays where it belongs.
 */
export function validateProfileInput(input: ProfileInput): ProfileValidation {
    const errors: ProfileValidation["errors"] = {};
    const mappingErrors: ProfileValidation["mappingErrors"] = {};

    if (!Number.isInteger(input.group_id) || input.group_id <= 0) {
        errors.group = "Choose an audience to import into.";
    }

    const source = validateSource(input.app_id, input.source);
    if (!source.valid) errors.source = "Check the source details.";

    for (const [key, value] of Object.entries(input.mapping ?? {})) {
        const field = key as ContactField;
        if (!CONTACT_FIELDS.includes(field)) {
            errors.mapping = "This mapping has a field that is not supported.";
            continue;
        }
        const text = `${value ?? ""}`;
        if (!text.trim()) {
            mappingErrors[field] = "Remove this row or give it a provider field.";
        } else if (text.length > MAX_IDENTIFIER_LENGTH) {
            mappingErrors[field] = `Must be ${MAX_IDENTIFIER_LENGTH} characters or fewer.`;
        } else if (CONTROL_CHARS.test(text)) {
            mappingErrors[field] = "Contains characters that are not allowed.";
        }
    }
    if (Object.keys(mappingErrors).length) errors.mapping = errors.mapping ?? "Check the field mapping.";

    const permission = validatePermission(input.permission);
    if (permission) errors.permission = permission;

    if (!FREQUENCIES.includes(input.frequency)) {
        errors.frequency = "Choose how often this should sync.";
    }

    return {
        valid: Object.keys(errors).length === 0,
        errors,
        sourceErrors: source.errors,
        mappingErrors,
    };
}

/** Returns a message when the permission policy is unusable, else null. */
export function validatePermission(permission: Permission | undefined): string | null {
    if (!permission?.mode) return "Choose how you have permission to contact these people.";

    if (permission.mode === "source") return null;

    if (permission.mode === "mapped") {
        const field = permission.field?.trim() ?? "";
        if (!field) return "Name the provider field that holds the opt-in evidence.";
        if (field.length > MAX_IDENTIFIER_LENGTH) {
            return `Must be ${MAX_IDENTIFIER_LENGTH} characters or fewer.`;
        }
        if (CONTROL_CHARS.test(field)) return "Contains characters that are not allowed.";
        return null;
    }

    if (permission.mode === "attested") {
        const text = permission.attestation?.trim() ?? "";
        if (text.length < ATTESTATION_MIN) {
            return `Write at least ${ATTESTATION_MIN} characters describing how you have consent.`;
        }
        if (text.length > ATTESTATION_MAX) {
            return `Keep this under ${ATTESTATION_MAX} characters.`;
        }
        return null;
    }

    return "Choose how you have permission to contact these people.";
}

/** A fresh profile, with the handoff's defaults: no mapping, no schedule. */
export function emptyProfileInput(appId: ProfileInput["app_id"]): ProfileInput {
    return {
        app_id: appId,
        group_id: 0,
        source: {},
        mapping: {},
        permission: { mode: "source" },
        // "default/off does not schedule automatic work"
        frequency: "off",
    };
}

/** Human label for a profile status, kept in one place for consistency. */
export const PROFILE_STATUS_LABEL: Record<Profile["status"], string> = {
    enabled: "Enabled",
    disabled: "Disabled",
    paused_auth: "Paused — reconnect needed",
    paused_quota: "Paused — budget exhausted",
    failed: "Failed",
};
